/**
 * SettingsPanel — the per-game Settings tab. Two sections, one contract
 * ("save now, build later"; owner decision 2026-07-09):
 *
 *  - DESIGN: the GameDesign doc (the mechanics authority) via the same
 *    DesignDocEditor as the wizard's review step. Saving writes through
 *    `updateGameDesign` — no engineHint/style here (the starter family is
 *    template-locked once built; the gallery style has its own editor).
 *  - PLATFORM SETTINGS: the v4 registry decisions via DecisionControl,
 *    saved through `updateGameSettings`.
 *
 * The next build — whatever its prompt — applies both; the Iterate tab shows
 * the pending-changes banners meanwhile.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { schemeParamsFrom, validateKeyBindings, type KeyBindings } from '@djgg/controls';
import {
  SECTIONS,
  DECISIONS,
  DECISION_IDS,
  completeSettings,
  isVisible,
  sanitizeSettings,
  validateGameDesign,
  type Creation,
  type DecisionId,
  type GameDesign,
  type GameSettings,
  type PartialGameSettings,
} from '@djgg/creations';
import { useServices } from '../services/context.js';
import { useAttributedAction } from '../hooks/useAttributedAction.js';
import { Mascot } from '../components/Mascot.js';
import { DecisionControl } from './wizard/DecisionControl.js';
import { DesignDocEditor } from './wizard/DesignDocEditor.js';
import { ControlSchemeCards } from './ControlSchemeCards.js';
import { KeyRemapEditor } from './KeyRemapEditor.js';

/** First validateGameDesign error for a draft, or '' when valid. */
function designError(design: GameDesign): string {
  try {
    validateGameDesign(design);
    return '';
  } catch (err) {
    return err instanceof Error ? err.message : 'The design is incomplete.';
  }
}

export function SettingsPanel({ creation, initialSection }: { creation: Creation & { id: string }; initialSection?: string }) {
  const services = useServices();
  const { runAttributed, attributionModal } = useAttributedAction();

  // Pre-v4 answer sets sanitize to {} (their keys aren't v4 decisions) — the
  // creation reads as unanswered until the creator confirms a v4 set here.
  const stored = useMemo(() => sanitizeSettings(creation.settings).values, [creation.settings]);
  const [answers, setAnswers] = useState<PartialGameSettings>(stored);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [savedRev, setSavedRev] = useState<number | null>(null);
  // Keep the authority this mounted editor actually opened. A newer prop
  // snapshot must not silently let a stale tab overwrite that head.
  const expectedSettingsRev = useRef(creation.settingsRev ?? 0);

  // Active settings sub-tab id ('design' | a section id). Falls back to the
  // first available tab when the current one has no visible content.
  const [sub, setSub] = useState<string>(initialSection ?? 'design');
  useEffect(() => { if (initialSection) setSub(initialSection); }, [initialSection]);

  const [design, setDesign] = useState<GameDesign | null>(creation.gameDesign ?? null);
  const [designBusy, setDesignBusy] = useState(false);
  const [designServerError, setDesignServerError] = useState('');
  const [savedDesignRev, setSavedDesignRev] = useState<number | null>(null);
  const expectedDesignRev = useRef(creation.designRev ?? 0);

  const dirty = JSON.stringify(answers) !== JSON.stringify(stored);
  const designDirty = JSON.stringify(design) !== JSON.stringify(creation.gameDesign ?? null);
  const designDraftError = design ? designError(design) : '';
  const complete = completeSettings(answers);

  function answer(decision: DecisionId, value: GameSettings[DecisionId]) {
    setSavedRev(null);
    setAnswers((prev) => {
      // Sanitize the decision map only — an in-progress keyBindings remap
      // (possibly invalid, error shown) must survive sibling edits verbatim.
      const next = sanitizeSettings({ ...prev, [decision]: value }).values;
      return prev.keyBindings !== undefined ? { ...next, keyBindings: prev.keyBindings } : next;
    });
  }

  // keyBindings bypasses sanitize while editing (an in-progress invalid remap
  // must stay visible with its error, not silently drop); save is disabled
  // until it validates — the same rule the server enforces.
  const keyProblems = validateKeyBindings(answers.keyBindings ?? {});
  function setKeyBindings(next: Partial<KeyBindings> | undefined) {
    setSavedRev(null);
    setAnswers((prev) => {
      const rest = { ...prev } as Record<string, unknown>;
      delete rest['keyBindings'];
      return (next ? { ...rest, keyBindings: next } : rest) as PartialGameSettings;
    });
  }

  async function save() {
    setError('');
    setBusy(true);
    try {
      await runAttributed(creation, async (attributeTo) => {
        const result = await services.callable.updateGameSettings({
          creationId: creation.id,
          settings: answers,
          expectedRevision: expectedSettingsRev.current,
          attributeTo,
        });
        expectedSettingsRev.current = result.settingsRev;
        setSavedRev(result.settingsRev);
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings.');
    } finally {
      setBusy(false);
    }
  }

  async function saveDesign() {
    if (!design) return;
    setDesignServerError('');
    setDesignBusy(true);
    try {
      await runAttributed(creation, async (attributeTo) => {
        const result = await services.callable.updateGameDesign({
          creationId: creation.id,
          design: validateGameDesign(design),
          expectedRevision: expectedDesignRev.current,
          attributeTo,
        });
        expectedDesignRev.current = result.designRev;
        setSavedDesignRev(result.designRev);
      });
    } catch (err) {
      setDesignServerError(err instanceof Error ? err.message : 'Failed to save the design.');
    } finally {
      setDesignBusy(false);
    }
  }

  // Platform-settings sections that actually have a visible decision (a section
  // whose decisions are all hidden by visibility conditions is dropped, and so
  // is its sub-tab).
  const settingsSections = SECTIONS.map((section) => ({
    section,
    decisions: DECISION_IDS.filter(
      (id) => DECISIONS[id].section === section.id && isVisible(id, answers),
    ),
  })).filter((s) => s.decisions.length > 0);

  // Sub-tabs: the Design doc first (when present), then one per non-empty
  // section. Splitting the long stacked form into focused screens (owner ask
  // 2026-07-22) — the single `answers` object still backs every settings tab,
  // so "Save settings" from any of them persists the whole set.
  const subTabs: Array<{ id: string; label: string }> = [
    ...(design ? [{ id: 'design', label: 'Design' }] : []),
    ...settingsSections.map((s) => ({ id: s.section.id,
      label: s.section.id === 'players' ? 'Players & screen shape'
        : s.section.id === 'controls' ? 'Mobile controls & keys' : s.section.label })),
  ];
  const activeSub = subTabs.some((t) => t.id === sub) ? sub : (subTabs[0]?.id ?? 'design');

  /** The decision controls for one section (controls section is the runtime
   *  special-case: preview cards + key remap). */
  function renderSectionBody(sectionId: string, decisions: DecisionId[]) {
    if (sectionId === 'controls') {
      return (
        <>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            Choose this game’s default mobile control style. It applies on the next load; players can switch
            among controls the game supports without changing your default.
          </p>
          <ControlSchemeCards
            value={answers.touchScheme}
            params={schemeParamsFrom(complete)}
            inputScheme={complete.inputScheme}
            onPick={(id) => answer('touchScheme', id as GameSettings['touchScheme'])}
          />
          {decisions
            .filter((id) => id !== 'touchScheme')
            .map((id) => (
              <DecisionControl key={id} id={id} value={answers[id] ?? complete[id]} onAnswer={answer} />
            ))}
          <KeyRemapEditor value={answers.keyBindings} onChange={setKeyBindings} />
        </>
      );
    }
    return (
      <>
        {decisions.map((id) => (
          <DecisionControl key={id} id={id} value={answers[id] ?? complete[id]} onAnswer={answer} />
        ))}
      </>
    );
  }

  return (
    <div className="panel" data-testid="settings-panel">
      <div className="overview-hero">
        <div>
          <h2 className="panel__title" style={{ marginTop: 0 }}>
            Settings — {creation.title}
          </h2>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            Changes save to the game and apply on the next build (see the Iterate tab).
          </p>
          <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
            Portrait / landscape is under Players &amp; screen shape.
            {' '}Touch layouts and keyboard bindings are under Mobile controls &amp; keys.
          </p>
        </div>
        <Mascot context="settings" size={190} quip={false} />
      </div>

      <div className="tabs tabs--sub" role="tablist" style={{ margin: '14px 0 4px' }}>
        {subTabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={activeSub === t.id}
            className={`tab ${activeSub === t.id ? 'tab--active' : ''}`}
            onClick={() => setSub(t.id)}
            data-testid={`settings-subtab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeSub === 'design' && design && (
        <section style={{ marginTop: 12 }} data-testid="settings-design">
          <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>Game design — the mechanics contract</h3>
          <DesignDocEditor
            design={design}
            baseline={creation.gameDesign ?? null}
            onChange={(d) => {
              setSavedDesignRev(null);
              setDesign(d);
            }}
            error={designDirty ? designDraftError || undefined : undefined}
            disabled={designBusy}
          />
          {designServerError && <div className="error-text">{designServerError}</div>}
          {savedDesignRev !== null && !designDirty && (
            <p className="muted" style={{ fontSize: 13 }} data-testid="design-saved">
              Design saved — your next build will follow it.
            </p>
          )}
          <button
            className="btn btn--primary"
            onClick={() => void saveDesign()}
            disabled={designBusy || !designDirty || designDraftError !== ''}
            data-testid="design-save"
          >
            {designBusy ? 'Saving…' : 'Save design'}
          </button>
        </section>
      )}

      {settingsSections
        .filter(({ section }) => section.id === activeSub)
        .map(({ section, decisions }) => (
          <Fragment key={section.id}>
            <section
              style={{ marginTop: 12 }}
              data-testid={section.id === 'controls' ? 'settings-controls' : undefined}
            >
              <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>{section.label}</h3>
              {renderSectionBody(section.id, decisions)}
            </section>

            {error && <div className="error-text">{error}</div>}
            {savedRev !== null && !dirty && (
              <p className="muted" style={{ fontSize: 13 }} data-testid="settings-saved">
                Saved — your next build will use these settings.
              </p>
            )}
            <button
              className="btn btn--primary"
              onClick={() => void save()}
              disabled={busy || !dirty || keyProblems.length > 0}
              data-testid="settings-save"
            >
              {busy ? 'Saving…' : 'Save settings'}
            </button>
          </Fragment>
        ))}
      {attributionModal}
    </div>
  );
}
